import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Chip,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DateRangeIcon from "@mui/icons-material/DateRange";
import TuneIcon from "@mui/icons-material/Tune";
import PublicIcon from "@mui/icons-material/Public";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SearchIcon from "@mui/icons-material/Search";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import type { CountryCount, FilterPanelState } from "@/types";
import {
  colors,
  accordionStyles,
  accordionSummaryStyles,
  accordionDetailsStyles,
  scoreSliderSx,
} from "@/theme/eventsTokens.ts";
import { FilterBadge, ToggleSection } from "./FilterWidgets.tsx";
import { countryName } from "@/utils/countryName.ts";

interface PanelProps {
  readonly expanded: boolean;
  readonly onToggle: (_event: React.SyntheticEvent, isExpanded: boolean) => void;
  readonly filters: FilterPanelState;
  readonly onFiltersChange: (filters: FilterPanelState) => void;
  readonly retentionMinDate?: Date;
  readonly retentionDays?: number;
}

export function DateRangePanel({ expanded, onToggle, filters, onFiltersChange, retentionMinDate, retentionDays }: PanelProps) {
  const badge = filters.startDate || filters.endDate ? 1 : 0;
  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-date">
        <FilterBadge
          count={badge}
          icon={<DateRangeIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Date Range"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <DatePicker
              label="Start"
              value={filters.startDate}
              onChange={(v) => onFiltersChange({ ...filters, startDate: v })}
              minDate={retentionMinDate}
              maxDate={filters.endDate || undefined}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear", "today"] },
              }}
            />
            <DatePicker
              label="End"
              value={filters.endDate}
              onChange={(v) => onFiltersChange({ ...filters, endDate: v })}
              minDate={filters.startDate || retentionMinDate}
              slotProps={{
                textField: { size: "small", fullWidth: true },
                actionBar: { actions: ["clear", "today"] },
              }}
            />
          </Box>
          {retentionDays && <Typography sx={{ fontSize: "0.75rem", color: colors.textMuted, mt: 1 }}>Data available for the last {retentionDays} days</Typography>}
        </LocalizationProvider>
      </AccordionDetails>
    </Accordion>
  );
}

interface ScorePanelProps extends PanelProps {
  readonly scoreRange: number[];
  readonly onScoreChange: (_event: Event, newValue: number | number[]) => void;
  readonly onScoreCommit: (_event: React.SyntheticEvent | Event, newValue: number | number[]) => void;
}

export function ScoreRangePanel({ expanded, onToggle, filters, scoreRange, onScoreChange, onScoreCommit }: ScorePanelProps) {
  const badge = filters.minScore > 0 || filters.maxScore < 100 ? 1 : 0;
  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-score">
        <FilterBadge
          count={badge}
          icon={<TuneIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Score Range"
        />
        {(filters.minScore > 0 || filters.maxScore < 100) && (
          <Chip
            label={`${scoreRange[0]}-${scoreRange[1]}`}
            size="small"
            sx={{ ml: "auto", mr: 1, height: 20, fontSize: "0.7rem" }}
          />
        )}
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <Box sx={{ px: 1 }}>
          <Slider
            value={scoreRange}
            onChange={onScoreChange}
            onChangeCommitted={onScoreCommit}
            valueLabelDisplay="auto"
            min={0}
            max={100}
            marks={[
              { value: 0, label: "0" },
              { value: 50, label: "50" },
              { value: 100, label: "100" },
            ]}
            sx={scoreSliderSx}
            data-testid="score-slider"
          />
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

interface CountryPanelProps extends PanelProps {
  readonly countryOptions?: CountryCount[];
}

export function CountryPanel({ expanded, onToggle, filters, onFiltersChange, countryOptions }: CountryPanelProps) {
  const badge = filters.countryCodes.length;

  // Build a count map from metadata for quick lookup
  const countMap = new Map<string, number>();
  if (countryOptions) {
    for (const c of countryOptions) {
      countMap.set(c.country_code, c.count);
    }
  }

  const options = countryOptions ? countryOptions.map((c) => c.country_code) : [];

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-country">
        <FilterBadge
          count={badge}
          icon={<PublicIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Countries"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <Autocomplete
          multiple
          size="small"
          loading={countryOptions === undefined}
          options={options}
          value={filters.countryCodes}
          onChange={(_e, v) => onFiltersChange({ ...filters, countryCodes: v })}
          getOptionLabel={(opt) => {
            const name = countryName(opt);
            const count = countMap.get(opt);
            return count !== undefined ? `${name} (${count.toLocaleString()})` : `${name} (${opt})`;
          }}
          renderValue={(value, getItemProps) =>
            value.map((opt, index) => {
              const { key: itemKey, ...itemProps } = getItemProps({ index });
              return <Chip {...itemProps} key={itemKey} label={opt} size="small" sx={{ height: 22, fontSize: "0.75rem" }} />;
            })
          }
          renderInput={(params) => <TextField {...params} placeholder="Search countries…" />}
          limitTags={2}
        />
      </AccordionDetails>
    </Accordion>
  );
}

function FailureSubtypes({ filters, onFiltersChange }: Pick<PanelProps, "filters" | "onFiltersChange">) {
  return (
    <Box sx={{ pl: 2, borderLeft: `2px solid ${colors.textMuted}33`, ml: 1 }}>
      <ToggleSection
        label="User Not Found"
        value={filters.hasUserNotFound}
        onChange={(v) => onFiltersChange({ ...filters, hasUserNotFound: v })}
      />
      <ToggleSection
        label="Invalid Password"
        value={filters.hasInvalidPassword}
        onChange={(v) => onFiltersChange({ ...filters, hasInvalidPassword: v })}
      />
      <ToggleSection
        label="Failed Second Factor"
        value={filters.hasFailedSecondFactor}
        onChange={(v) => onFiltersChange({ ...filters, hasFailedSecondFactor: v })}
      />
    </Box>
  );
}

export function EventStatusPanel({ expanded, onToggle, filters, onFiltersChange }: PanelProps) {
  const subtypeCount =
    (filters.hasUserNotFound !== null ? 1 : 0) +
    (filters.hasInvalidPassword !== null ? 1 : 0) +
    (filters.hasFailedSecondFactor !== null ? 1 : 0);
  const badge =
    (filters.hasBlockedAttempts !== null ? 1 : 0) +
    (filters.hasFailures !== null ? 1 : 0) +
    subtypeCount +
    (filters.hasSuccessfulAuth !== null ? 1 : 0);

  const handleFailuresChange = (v: boolean | null) => {
    if (v !== true) {
      onFiltersChange({
        ...filters,
        hasFailures: v,
        hasUserNotFound: null,
        hasInvalidPassword: null,
        hasFailedSecondFactor: null,
      });
    } else {
      onFiltersChange({ ...filters, hasFailures: v });
    }
  };

  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-status">
        <FilterBadge
          count={badge}
          icon={<CheckCircleIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="Event Status"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <ToggleSection
          label="Blocked Attempts"
          value={filters.hasBlockedAttempts}
          onChange={(v) => onFiltersChange({ ...filters, hasBlockedAttempts: v })}
        />
        <ToggleSection
          label="Authentication Failures"
          value={filters.hasFailures}
          onChange={handleFailuresChange}
        />
        {filters.hasFailures === true && <FailureSubtypes filters={filters} onFiltersChange={onFiltersChange} />}
        <ToggleSection
          label="Successful Authentication"
          value={filters.hasSuccessfulAuth}
          onChange={(v) => onFiltersChange({ ...filters, hasSuccessfulAuth: v })}
        />
      </AccordionDetails>
    </Accordion>
  );
}

export function IpAddressPanel({ expanded, onToggle, filters, onFiltersChange }: PanelProps) {
  const badge = filters.ipSearch ? 1 : 0;
  return (
    <Accordion expanded={expanded} onChange={onToggle} sx={accordionStyles}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={accordionSummaryStyles} data-testid="panel-ip">
        <FilterBadge
          count={badge}
          icon={<SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />}
          label="IP Address"
        />
      </AccordionSummary>
      <AccordionDetails sx={accordionDetailsStyles}>
        <TextField
          fullWidth
          size="small"
          placeholder="e.g. 192.168. or 1.2.3.4"
          value={filters.ipSearch}
          onChange={(e) => onFiltersChange({ ...filters, ipSearch: e.target.value })}
          slotProps={{
            htmlInput: {
              "aria-label": "IP address or prefix",
              "data-testid": "ip-filter-input",
              maxLength: 45,
            },
          }}
        />
        <Typography sx={{ fontSize: "0.75rem", color: colors.textMuted, mt: 1 }}>Full IP matches exactly; partial (e.g. <code>10.0.</code>) matches any IP starting with it.</Typography>
      </AccordionDetails>
    </Accordion>
  );
}
